// Press kit & asset machinery shared by the comms project detail page and
// (step 2) the theme pages: Dropbox folder browsing, thumbnails/lightbox,
// select mode, and assigning selected assets to a social post.
// Extracted from CommsPage.jsx unchanged, plus the usePressFolderBrowser /
// useAssetSelection hooks and SelectBar that generalize the browsing and
// selection state that previously lived inline in ProjectDetail.
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ChevronRight, ChevronDown, Eye, EyeOff, GripVertical, Film, File, FileText, FileSpreadsheet, FileArchive, FileImage, FileVideo, Presentation, PenTool } from 'lucide-react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { supabase } from '../../supabaseClient';
import { commsInsert, commsUpsert, commsUpdate, commsDelete } from './commsUtils';
import { VERSION_PREF_AUTO, chosenVariant, filterVariantFolders, skippedVariantNames } from './assetVariants';
import { S, GOLD, NoteArea, CommsModal, Field, CInput, FilterBtn, BtnGhost, BtnPrimary } from './commsUi';
import { createLimiter } from './concurrency';
import { ThrottledImg } from './thumbLoader';

const comms = supabase.schema('comms');

export const BRIO_PRESS_BASE = '/Architecture Brio/BRIO-press';
export const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;

export const PLATFORMS = ['Instagram Posts', 'Instagram Stories', 'LinkedIn'];

// Model scales are written with underscores, never colons. The real Dropbox
// folders are 1_100 / 1_200, and a ":" cannot survive the round trip anyway —
// macOS has treated it as a path separator since HFS, so Finder shows it back
// as "/". A default that disagreed with the tree is not harmless: the audit
// reports the real folder as unexpected and the imaginary one as missing, and
// create-press-folders would try to make a folder literally called "1:200".
// That mismatch is what got Models/1:200 renamed, orphaning three tagged
// drawings whose rows had to be deleted in the dropbox_file_id retrofit.
//
// There is no Design Process section: Renders and Sketches, the only two things
// it ever held across the estate, moved under Drawings in September 2026 and
// the section was retired. Tags and ranks are keyed on dropbox_file_id, not
// path, so the curation on those files followed them through the move. Removing
// it from this constant is not enough on its own: the live tree is the
// pressKitFolderSchema row in app_settings, and this list is only the fallback
// for a missing row.
export const PRESS_KIT_DEFAULT_SCHEMA = [
  { key: 'release',      label: 'Press Release',       folder: 'Press Release',       subfolders: [],                                          subfolderTemplate: [] },
  { key: 'photos',       label: 'Photography',          folder: 'Photography',          subfolders: [],                                          subfolderTemplate: [] },
  { key: 'drawings',     label: 'Drawings',             folder: 'Drawings',             subfolders: ['Plans', 'Sections', 'Elevations', 'Masterplan'], subfolderTemplate: [] },
  { key: 'models',       label: 'Models',               folder: 'Models',               subfolders: ['1_100', '1_200'],                          subfolderTemplate: [] },
  { key: 'construction', label: 'Construction Process', folder: 'Construction Process', subfolders: [],                                          subfolderTemplate: [] },
  { key: 'publications', label: 'Publications',          folder: 'Publications',          subfolders: [],                                          subfolderTemplate: [] },
];

export function formatFileSize(bytes) {
  if (bytes == null) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function isPdf(name) {
  return name.split('.').pop()?.toLowerCase() === 'pdf';
}

const VIDEO_EXTS = new Set(['mp4', 'mov', 'webm', 'm4v']);
export function isVideoFile(name) {
  return VIDEO_EXTS.has(name.split('.').pop()?.toLowerCase() || '');
}

// Folder listings are the expensive call on these pages: each one is a
// Dropbox round trip plus a thumbnail batch that uploads into Storage, and
// the expand-all cascade and the review mode both ask for every folder in a
// kit. Fired all at once they saturate the Storage API's database pool and
// the thumbnails that follow come back 429 (see thumbLoader.jsx). Every
// listing call goes through this gate, so a kit opens folder by folder.
export const LISTING_CONCURRENCY = 3;
export const withListingSlot = createLimiter(LISTING_CONCURRENCY);

export async function callEdgeFunction(functionName, body) {
  const { data: { session } } = await supabase.auth.getSession();
  const res = await fetch(`${SUPABASE_URL}/functions/v1/${functionName}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token}` },
    body: JSON.stringify(body),
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.error || json.message || json.msg || `${functionName} failed (HTTP ${res.status})`);
  return json;
}

// Reads a press-kit file's bytes. Dropbox temporary links normally allow CORS
// reads; when they don't, the edge function streams the bytes back instead
// (same fallback the social-post export uses).
export async function fetchPressFileBlob({ url, path }) {
  if (url) {
    try {
      const res = await fetch(url);
      if (res.ok) return await res.blob();
    } catch { /* fall through to the proxy */ }
  }
  if (!path) throw new Error('Could not load this file.');
  const { data: { session } } = await supabase.auth.getSession();
  const res = await fetch(`${SUPABASE_URL}/functions/v1/get-press-file-link`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token}` },
    body: JSON.stringify({ path, proxy: true }),
  });
  if (!res.ok) throw new Error('Could not load this file.');
  return await res.blob();
}

// Temporary download links are no longer fetched for every previewable file in
// a listing — that was one Dropbox API call per image and the dominant cost of
// opening a photo folder. The link for the one file somebody actually opens is
// resolved here instead, and kept for the session so paging back and forth
// through a lightbox doesn't re-ask.
const LINK_TTL_MS = 3 * 60 * 60 * 1000; // Dropbox temporary links last ~4h
const linkCache = new Map(); // dropbox path -> { url, at }

async function resolveFileUrl(path) {
  const hit = linkCache.get(path);
  if (hit && Date.now() - hit.at < LINK_TTL_MS) return hit.url;
  const { link } = await callEdgeFunction('get-press-file-link', { path });
  linkCache.set(path, { url: link, at: Date.now() });
  return link;
}

// The entry's own link when the listing carried one, otherwise a link fetched
// for this file alone. Exported for the review mode, which shows the real file
// rather than a thumbnail once the grid is down to one or two across.
export function useFileUrl(entry) {
  const direct = entry?.url || '';
  const path = entry?.path || '';
  const [state, setState] = useState({ url: direct, error: null });

  useEffect(() => {
    if (direct) { setState({ url: direct, error: null }); return; }
    if (!path) { setState({ url: '', error: null }); return; }
    let cancelled = false;
    setState({ url: '', error: null });
    resolveFileUrl(path)
      .then(url => { if (!cancelled) setState({ url, error: null }); })
      .catch(e => { if (!cancelled) setState({ url: '', error: e.message }); });
    return () => { cancelled = true; };
  }, [direct, path]);

  return state;
}

// Dropbox serves its temporary links with `Content-Disposition: attachment`,
// which makes the browser download a PDF instead of previewing it — even inside
// an <iframe>. Pulling the bytes down and handing the iframe an
// `application/pdf` blob URL keeps the preview inline.
function usePdfPreview(entry, resolvedUrl) {
  const isPdfEntry = !!entry?.isPdf;
  const url = resolvedUrl || '';
  const path = entry?.path || '';
  const [state, setState] = useState({ url: null, error: null });

  useEffect(() => {
    if (!isPdfEntry || (!url && !path)) { setState({ url: null, error: null }); return; }
    let cancelled = false;
    let objectUrl = null;
    setState({ url: null, error: null });
    fetchPressFileBlob({ url, path })
      .then(blob => {
        if (cancelled) return;
        // Dropbox labels the body application/octet-stream; the viewer needs the
        // PDF type on the blob to render it rather than offer a save.
        objectUrl = URL.createObjectURL(blob.type === 'application/pdf' ? blob : new Blob([blob], { type: 'application/pdf' }));
        setState({ url: objectUrl, error: null });
      })
      .catch(e => { if (!cancelled) setState({ url: null, error: e.message }); });
    return () => { cancelled = true; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [isPdfEntry, url, path]);

  return state;
}

export function Lightbox({ entries, index, onClose, rankView }) {
  const [currentIdx, setCurrentIdx] = useState(index ?? 0);
  useEffect(() => { setCurrentIdx(index ?? 0); }, [entries, index]);

  useEffect(() => {
    if (!entries?.length) return;
    const onKey = e => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft')  setCurrentIdx(i => Math.max(0, i - 1));
      if (e.key === 'ArrowRight') setCurrentIdx(i => Math.min(entries.length - 1, i + 1));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [entries, onClose]);

  const entry = entries?.length ? entries[Math.min(currentIdx, entries.length - 1)] : null;

  // 1–5 rates the open image and 0 clears it, the shortcut every culling tool
  // uses. Kept separate from the navigation keys above so it can depend on the
  // open entry without re-binding those on every step through the set.
  const rate = rankView?.onRate;
  const rateableId = entry?.fileId;
  useEffect(() => {
    if (!rate || !rateableId) return;
    const onKey = e => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (!/^[0-5]$/.test(e.key)) return;
      e.preventDefault();
      rate(entry, Number(e.key));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [rate, rateableId, entry]);

  // Previewable files arrive without a link; this fetches one for the open file.
  const fileUrl = useFileUrl(entry);
  const pdfPreview = usePdfPreview(entry, fileUrl.url);

  if (!entries?.length) return null;
  const hasPrev = currentIdx > 0;
  const hasNext = currentIdx < entries.length - 1;
  const navBtn = (side, onClick) => (
    <button onClick={onClick}
      style={{ position: 'absolute', [side]: 12, top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.12)', border: 'none', color: '#fff', fontSize: 36, lineHeight: 1, cursor: 'pointer', borderRadius: 4, padding: '6px 14px', zIndex: 1 }}>
      {side === 'left' ? '‹' : '›'}
    </button>
  );
  return (
    <div onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 9999, backgroundColor: 'rgba(0,0,0,0.88)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <button onClick={onClose}
        style={{ position: 'absolute', top: 16, right: 20, background: 'none', border: 'none', color: '#fff', fontSize: 28, lineHeight: 1, cursor: 'pointer', opacity: 0.7 }}>
        ×
      </button>
      {rate && entry && !entry.isPdf && (
        <div onClick={e => e.stopPropagation()}
          title="Press 1–5 to rank, 0 to clear"
          style={{ position: 'absolute', top: 14, right: 58, zIndex: 2, display: 'flex', alignItems: 'center', gap: 8, background: 'rgba(0,0,0,.35)', borderRadius: 4, padding: '5px 10px' }}>
          <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 9, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(255,255,255,.55)' }}>Rank</span>
          {rateableId
            ? <StarPicker onDark size={18} value={rankView.byId?.[rateableId] || 0} onChange={r => rate(entry, r)} />
            : <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, color: 'rgba(255,255,255,.55)' }}>reload the folder first</span>}
        </div>
      )}
      {hasPrev && navBtn('left',  e => { e.stopPropagation(); setCurrentIdx(i => i - 1); })}
      {hasNext && navBtn('right', e => { e.stopPropagation(); setCurrentIdx(i => i + 1); })}
      <div onClick={e => e.stopPropagation()} style={{ maxWidth: '84vw', maxHeight: '92vh', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
        {entry.isVideo
          ? <video src={fileUrl.url} controls autoPlay
              style={{ maxWidth: '84vw', maxHeight: '84vh', borderRadius: 2, display: 'block', outline: 'none' }} />
          : entry.isPdf
            ? (
              <div style={{ width: '84vw', height: '84vh', borderRadius: 2, background: '#F7F6F2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {pdfPreview.url
                  ? <iframe src={pdfPreview.url} title={entry.name}
                      style={{ width: '100%', height: '100%', border: 'none', borderRadius: 2 }} />
                  : <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 12, color: '#6E6C66', textAlign: 'center', padding: 20 }}>
                      {pdfPreview.error
                        ? <>
                            {pdfPreview.error}
                            {fileUrl.url && (
                              <>
                                <br />
                                <a href={fileUrl.url} target="_blank" rel="noreferrer" style={{ color: GOLD }}>Open the PDF in a new tab</a>
                              </>
                            )}
                          </>
                        : 'Loading preview…'}
                    </div>
                }
              </div>
            )
            : fileUrl.url
              ? <img src={fileUrl.url} alt={entry.name}
                  style={{ maxWidth: '84vw', maxHeight: '84vh', objectFit: 'contain', borderRadius: 2, display: 'block' }} />
              : <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 12, color: 'rgba(255,255,255,0.65)', padding: 40 }}>
                  {fileUrl.error || 'Loading…'}
                </div>
        }
        <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, color: 'rgba(255,255,255,0.55)', textAlign: 'center', display: 'flex', alignItems: 'center', gap: 10 }}>
          <span>{entry.name}{entries.length > 1 ? ` · ${currentIdx + 1}/${entries.length}` : ''}</span>
          {entry.isPdf && fileUrl.url && (
            <a href={fileUrl.url} target="_blank" rel="noreferrer" style={{ color: 'rgba(255,255,255,0.75)' }}>Download</a>
          )}
        </span>
      </div>
    </div>
  );
}

/**
 * Sort a folder listing by name. Dropbox returns list_folder entries in no
 * defined order, so photographs arrived shuffled and a folder could look
 * different on every visit.
 *
 * Numeric-aware, so `shot-2` comes before `shot-10` rather than after it —
 * photo filenames are full of unpadded counters. Case- and accent-insensitive,
 * so `IMG_01` and `img_02` interleave instead of forming two blocks.
 */
export function compareByName(a, b) {
  return (a?.name || '').localeCompare(b?.name || '', undefined, { numeric: true, sensitivity: 'base' });
}

// Files that belong in the picture grid rather than the plain file list.
// PDFs sit in the list: a grid tile could only draw a generic PDF glyph, so
// the name, which is what tells one PDF from another, was clipped to two lines.
export function isPreviewable(e) {
  return !!e.is_image || !!e.is_video;
}

// The icon and short label the file list puts in front of a name, so the type
// reads at a glance while the name itself is cut to one line.
const FILE_KINDS = [
  { exts: ['pdf'], Icon: FileText, color: '#B5452F' },
  { exts: ['doc', 'docx', 'pages', 'rtf', 'txt', 'odt', 'md'], Icon: FileText, color: '#2F5D9E' },
  { exts: ['xls', 'xlsx', 'numbers', 'csv', 'ods'], Icon: FileSpreadsheet, color: '#2E7D4F' },
  { exts: ['ppt', 'pptx', 'key', 'odp'], Icon: Presentation, color: '#C46A1B' },
  { exts: ['dwg', 'dxf', 'skp', 'rvt', '3dm', 'ai', 'indd', 'psd', 'eps', 'svg'], Icon: PenTool, color: '#6B4FA0' },
  { exts: ['zip', 'rar', '7z', 'tar', 'gz'], Icon: FileArchive, color: '#6E6C66' },
  { exts: ['jpg', 'jpeg', 'png', 'tif', 'tiff', 'heic', 'gif', 'webp'], Icon: FileImage, color: '#9F902A' },
  { exts: [...VIDEO_EXTS], Icon: FileVideo, color: '#9F902A' },
];
export function fileExtension(name) {
  const i = (name || '').lastIndexOf('.');
  return i === -1 ? '' : name.slice(i + 1).toLowerCase();
}
function FileTypeBadge({ name }) {
  const ext = fileExtension(name);
  const kind = FILE_KINDS.find(k => k.exts.includes(ext));
  const Icon = kind?.Icon || File;
  const color = kind?.color || '#9E9C96';
  return (
    <span title={ext ? `.${ext} file` : 'File'} style={{ display: 'inline-flex', alignItems: 'center', gap: 3, flexShrink: 0, width: 50 }}>
      <Icon size={13} color={color} strokeWidth={1.75} aria-hidden="true" />
      <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 8, letterSpacing: '0.04em', fontWeight: 600, color, textTransform: 'uppercase', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {ext || 'file'}
      </span>
    </span>
  );
}

// Thumbnails come from the comms-assets bucket as URLs. One is missing only
// while it is still being generated, or when Dropbox cannot render a preview
// for that file at all, so the tile keeps its shape and shows the name. The
// same name tile stands in when the thumbnail exists but cannot be fetched
// after every retry, instead of the browser's broken-image icon.
function ThumbImage({ entry, dimmed }) {
  const style = { width: '100%', aspectRatio: '3/2', objectFit: 'cover', display: 'block', opacity: dimmed ? 0.75 : 1 };
  // Transitional: a list-press-folder-contents deployment older than this build
  // still inlines base64. Keeps thumbnails visible if the frontend ships first;
  // drop once the function is deployed.
  const src = entry.thumb_url || (entry.thumbnail ? `data:image/jpeg;base64,${entry.thumbnail}` : null);
  const nameTile = (
    <div style={{ ...style, backgroundColor: '#E8E5DD', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 4 }}>
      <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 9, color: '#6E6C66', textAlign: 'center', wordBreak: 'break-all', lineHeight: 1.3, maxWidth: '100%', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{entry.name}</span>
    </div>
  );
  if (!src) return nameTile;
  return (
    <ThrottledImg src={src} alt={entry.name} style={style}
      placeholderStyle={{ backgroundColor: '#E8E5DD' }} fallback={nameTile} />
  );
}

function TagOverlay({ tags }) {
  if (!tags?.length) return null;
  return (
    <span style={{ position: 'absolute', left: 4, bottom: 4, right: 4, fontFamily: "'IBM Plex Mono', monospace", fontSize: 8, letterSpacing: '0.04em', background: 'rgba(27,27,25,.65)', color: '#F7F6F2', borderRadius: 2, padding: '2px 5px', lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: 'fit-content', maxWidth: 'calc(100% - 8px)' }}>
      {[...tags].sort((a, b) => a.localeCompare(b)).map(t => `#${t}`).join(' ')}
    </span>
  );
}

// `rankView` is optional: { byId, min, openRankMenu }. `byId` draws the star
// badges; a non-zero `min` hides everything ranked below it, which is how the
// shortlist filter works — a view over the same listing, not a separate fetch;
// `openRankMenu` turns on right-click-to-rank.
export function renderFileContents(sf, imgCols, onOpen, folderPath, selection, tagsByAsset, rankView) {
  if (!sf) return null;
  const metaStyle = { fontFamily: "'IBM Plex Mono', monospace", fontSize: 12, color: '#6E6C66', margin: 0 };
  if (sf.loading) return <div style={metaStyle}>Loading…</div>;
  if (sf.error) return <div style={{ ...metaStyle, color: '#C0392B' }}>{sf.error}</div>;
  const allEntries = sf.entries.filter(e => !e.is_folder);
  const rankMin = rankView?.min || 0;
  const rankOf = e => (e.file_id && rankView?.byId?.[e.file_id]) || 0;
  const nonFolderEntries = rankMin ? allEntries.filter(e => rankOf(e) >= rankMin) : allEntries;
  if (nonFolderEntries.length === 0) {
    // While filtering, a folder with nothing shortlisted says nothing at all —
    // the note would otherwise repeat under every folder in the kit.
    if (rankMin) return null;
    // A folder that only holds subfolders isn't empty — the note would just
    // repeat under every parent in the tree.
    if (sf.entries.some(e => e.is_folder)) return null;
    return <div style={metaStyle}>No files in this folder.</div>;
  }
  // What belongs in the picture grid is decided by the file type, never by
  // whether a thumbnail happens to have arrived: thumbnails are generated
  // lazily and cached, so keying the layout off them would drop an image into
  // the plain file list — and out of select mode — on its first ever view.
  // Alphabetical by default. filter() already copies, so this sorts the copy
  // and leaves the cached listing alone. lbEntries is built from previewEntries
  // below, so arrowing through the lightbox follows what is on screen.
  const previewEntries = nonFolderEntries.filter(isPreviewable).sort(compareByName);
  const fileEntries = nonFolderEntries.filter(e => !isPreviewable(e)).sort(compareByName);
  const selecting = !!selection?.active && !!folderPath;
  const pathOf = e => `${folderPath}/${e.name}`;
  // `path` lets the lightbox fall back to the edge-function proxy when a direct
  // read of the Dropbox link is blocked (see fetchPressFileBlob).
  const lbEntryOf = e => ({
    url: e.download_url,
    path: folderPath ? pathOf(e) : null,
    name: e.name,
    isPdf: isPdf(e.name),
    isVideo: !!e.is_video,
    // Lets the lightbox rank the open image; see StarPicker in Lightbox.
    fileId: e.file_id || null,
    thumbUrl: e.thumb_url || null,
  });
  const lbEntries = previewEntries.map(lbEntryOf);
  // PDFs open in the same lightbox from the file list, arrowing through the
  // folder's PDFs only.
  const pdfEntries = fileEntries.filter(e => isPdf(e.name));
  const pdfLbEntries = pdfEntries.map(lbEntryOf);
  // What a list row stores when selected. PDFs and videos have always been
  // selectable (themes take them); any other file only where the page asks
  // for it, and `isFile` lets posts and themes skip it.
  const listMetaOf = e => {
    if (isPdf(e.name)) return { name: e.name, thumbUrl: null, isVideo: false, isPdf: true, fileId: e.file_id || null };
    if (e.is_video || isVideoFile(e.name)) return { name: e.name, thumbUrl: null, isVideo: true, fileId: e.file_id || null };
    return selection?.allowFiles ? { name: e.name, thumbUrl: null, isVideo: false, isFile: true, fileId: e.file_id || null } : null;
  };
  const tagsOf = e => tagsForAsset(tagsByAsset, e.file_id, pathOf(e));
  // What the rank menu and the lightbox need to write a rank. projectCode is
  // added by the page, which is the only place that knows it.
  const assetOf = e => ({ path: pathOf(e), fileId: e.file_id || null, name: e.name, thumbUrl: e.thumb_url || null, isVideo: !!e.is_video });
  const onCtx = rankView?.openRankMenu ? (e => ev => rankView.openRankMenu(ev, assetOf(e))) : null;
  return (
    <>
      {previewEntries.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${imgCols}, 1fr)`, gap: 4, marginBottom: fileEntries.length ? 8 : 0 }}>
          {previewEntries.map((e, i) => {
            const selectable = selecting && isPreviewable(e);
            const selected = selectable && selection.has(pathOf(e));
            const handleClick = selectable
              ? (ev => { ev.preventDefault(); selection.toggle(pathOf(e), { name: e.name, thumbUrl: e.thumb_url || null, isVideo: !!e.is_video, fileId: e.file_id || null }); })
              : onOpen ? (ev => { ev.preventDefault(); onOpen({ entries: lbEntries, index: i, folderPath }); }) : null;
            if (e.is_video) {
              return (
                <div key={e.name} onClick={handleClick || undefined}
                  onContextMenu={onCtx ? onCtx(e) : undefined}
                  title={e.name}
                  style={{ borderRadius: 2, overflow: 'hidden', lineHeight: 0, cursor: handleClick ? 'pointer' : 'default', position: 'relative', outline: selected ? `2px solid ${GOLD}` : 'none', outlineOffset: -2 }}>
                  <ThumbImage entry={e} dimmed={selecting && !selected} />
                  <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.22)' }}>
                    <svg width="10" height="12" viewBox="0 0 10 12" fill="none"><path d="M1 1l8 5-8 5V1z" fill="white"/></svg>
                  </div>
                  {selectable && (
                    <span style={{ position: 'absolute', top: 4, right: 4, width: 16, height: 16, borderRadius: '50%', background: selected ? GOLD : 'rgba(255,255,255,.75)', border: selected ? 'none' : '1px solid rgba(27,27,25,.3)', color: '#fff', fontSize: 11, lineHeight: '16px', textAlign: 'center', fontFamily: "'IBM Plex Mono', monospace" }}>
                      {selected ? '✓' : ''}
                    </span>
                  )}
                  <TagOverlay tags={tagsOf(e)} />
                  <RankStars rank={rankOf(e)} />
                </div>
              );
            }
            return (
              <div key={e.name} onClick={handleClick || undefined}
                onContextMenu={onCtx ? onCtx(e) : undefined}
                title={e.name}
                style={{ position: 'relative', borderRadius: 2, overflow: 'hidden', lineHeight: 0, cursor: handleClick ? 'pointer' : 'default', outline: selected ? `2px solid ${GOLD}` : 'none', outlineOffset: -2 }}>
                <ThumbImage entry={e} dimmed={selecting && !selected} />
                {selectable && (
                  <span style={{ position: 'absolute', top: 4, right: 4, width: 16, height: 16, borderRadius: '50%', background: selected ? GOLD : 'rgba(255,255,255,.75)', border: selected ? 'none' : '1px solid rgba(27,27,25,.3)', color: '#fff', fontSize: 11, lineHeight: '16px', textAlign: 'center', fontFamily: "'IBM Plex Mono', monospace" }}>
                    {selected ? '✓' : ''}
                  </span>
                )}
                <TagOverlay tags={tagsOf(e)} />
                <RankStars rank={rankOf(e)} />
              </div>
            );
          })}
        </div>
      )}
      {fileEntries.length > 0 && (
        <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
          {fileEntries.map(e => {
            const meta = selecting ? listMetaOf(e) : null;
            const selected = !!meta && selection.has(pathOf(e));
            const toggle = meta ? () => selection.toggle(pathOf(e), meta) : null;
            const pdfIdx = isPdf(e.name) ? pdfEntries.indexOf(e) : -1;
            // In select mode the name selects, like a tile does. Otherwise a
            // PDF opens in the lightbox and anything else downloads.
            const onNameClick = toggle
              ? (ev => { ev.preventDefault(); toggle(); })
              : (pdfIdx !== -1 && onOpen)
                ? (ev => { ev.preventDefault(); onOpen({ entries: pdfLbEntries, index: pdfIdx, folderPath }); })
                : null;
            const href = toggle ? undefined : (e.download_url || undefined);
            const nameStyle = { fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, color: (href || onNameClick) ? '#21302A' : '#6E6C66', textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0, flex: '0 1 auto', cursor: (href || onNameClick) ? 'pointer' : 'default' };
            return (
              <li key={e.name} onContextMenu={onCtx ? onCtx(e) : undefined}
                style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3, minWidth: 0 }}>
                {toggle && (
                  <input
                    type="checkbox"
                    checked={selected}
                    onChange={toggle}
                    aria-label={`Select ${e.name}`}
                    style={{ accentColor: GOLD, cursor: 'pointer', flexShrink: 0, margin: 0 }}
                  />
                )}
                <FileTypeBadge name={e.name} />
                {(href || onNameClick)
                  ? <a href={href} onClick={onNameClick || undefined} target={href ? '_blank' : undefined} rel="noreferrer" title={e.name}
                      style={nameStyle}
                      onMouseEnter={ev => { ev.currentTarget.style.textDecoration = 'underline'; }}
                      onMouseLeave={ev => { ev.currentTarget.style.textDecoration = 'none'; }}>{e.name}</a>
                  : <span title={e.name} style={nameStyle}>{e.name}</span>
                }
                <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, color: '#9E9C96', flexShrink: 0 }}>{formatFileSize(e.size)}</span>
                {tagsOf(e)?.length > 0 && (
                  <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 9, color: GOLD, flexShrink: 0 }}>{tagsOf(e).map(t => `#${t}`).join(' ')}</span>
                )}
                {rankOf(e) > 0 && (
                  <span title={`${rankOf(e)} of 5`} style={{ fontSize: 9, color: GOLD, flexShrink: 0 }}>{'\u2605'.repeat(rankOf(e))}</span>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </>
  );
}

// A folder's name in the kit. With `onOpenFolder` it is a button that opens
// the review mode on everything under that folder; the chevron beside it
// keeps expanding and collapsing the listing in place.
export function FolderName({ name, path, color, onOpenFolder }) {
  const style = { ...S.eyebrow, fontSize: 9, color };
  if (!onOpenFolder) return <span style={style}>{name}</span>;
  // `font: inherit` strips the button's UA font and has to come first: a
  // spread key keeps its original position, so a `font` shorthand placed after
  // the spread was applied last and reset the mono family and size.
  return (
    <button type="button" onClick={() => onOpenFolder(path)}
      title="Preview the images in this folder"
      style={{ font: 'inherit', ...style, background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left' }}>
      {name}
    </button>
  );
}

export function FolderTree({ folderKey, folderPath, sectionFiles, sectionExpanded, onToggleFiles, versionPref, imgCols, onOpenLightbox, onOpenFolder, selection, tagsByAsset, rankView }) {
  const sf = sectionFiles[folderKey];
  if (!sf) return null;
  const metaStyle = { fontFamily: "'IBM Plex Mono', monospace", fontSize: 12, color: '#6E6C66', margin: 0 };
  if (sf.loading) return <div style={metaStyle}>Loading…</div>;
  if (sf.error) return <div style={{ ...metaStyle, color: '#C0392B' }}>{sf.error}</div>;
  const subFolders = (sf.entries || []).filter(e => {
    if (!e.is_folder) return false;
    const childSf = sectionFiles[`${folderKey}__${e.name}`];
    return !(childSf && !childSf.loading && childSf.entries && childSf.entries.length === 0);
  });
  // Of a run of same-images-different-size siblings, only the version the
  // "Image versions" setting asks for gets a row; the rest are never fetched.
  // Sorted first: these all come from Dropbox, so they have no order of their
  // own. filterVariantFolders ranks by name rather than by position, so
  // reordering ahead of it cannot change which variant wins.
  const shownFolders = filterVariantFolders([...subFolders].sort(compareByName), f => f.name, versionPref);
  return (
    <>
      {shownFolders.map(folder => {
        const childKey = `${folderKey}__${folder.name}`;
        const childPath = `${folderPath}/${folder.name}`;
        const childExpanded = !!sectionExpanded[childKey];
        return (
          <div key={folder.name} style={{ paddingTop: 4, borderTop: '1px solid rgba(27,27,25,.06)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: childExpanded ? 4 : 0 }}>
              <FolderName name={folder.name} path={childPath} color="#9E9C96" onOpenFolder={onOpenFolder} />
              <button onClick={() => onToggleFiles(childKey, childPath)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 1, color: '#9E9C96', lineHeight: 1 }}>
                {childExpanded ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
              </button>
            </div>
            {childExpanded && (
              <div style={{ paddingLeft: 8, marginBottom: 4 }}>
                <FolderTree
                  folderKey={childKey}
                  folderPath={childPath}
                  sectionFiles={sectionFiles}
                  sectionExpanded={sectionExpanded}
                  onToggleFiles={onToggleFiles}
                  versionPref={versionPref}
                  imgCols={imgCols}
                  onOpenLightbox={onOpenLightbox}
                  onOpenFolder={onOpenFolder}
                  selection={selection}
                  tagsByAsset={tagsByAsset}
                  rankView={rankView}
                />
              </div>
            )}
          </div>
        );
      })}
      {renderFileContents(sf, imgCols, onOpenLightbox, folderPath, selection, tagsByAsset, rankView)}
    </>
  );
}

/**
 * Template folders (JPEG-web, TIFF-highres, …) the schema wants inside `subName`
 * but which its listing does not show.
 *
 * This is free: the page has already fetched the subfolder's contents to display
 * them, so noticing the gap costs no Dropbox call — only filling it does.
 * Returns [] while the listing is still loading or was never opened, so the
 * offer appears exactly when we can be sure it is warranted.
 */
function missingTemplateFolders(sectionSchema, sf) {
  const template = sectionSchema?.subfolderTemplate || [];
  if (template.length === 0) return [];
  if (!sf || sf.loading || sf.error || !sf.entries) return [];
  const have = new Set(sf.entries.filter(e => e.is_folder).map(e => e.name.toLowerCase()));
  return template.filter(t => !have.has(t.toLowerCase()));
}

export function KitCard({ section, label, dropboxFolder, kit, sectionSchema, approvedSubfolders, manageMode, isHidden, onToggleHide, sectionExpanded, sectionFiles, onToggleFiles, versionPref, saveKit, projectFolderName, imgCols, onOpenLightbox, onOpenFolder, selection, tagsByAsset, rankView, onFillTemplate, fillingTemplate }) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id: section, disabled: !manageMode });
  const mainSf = sectionFiles[section];
  const expanded = !!sectionExpanded[section];
  const hasLink = !!kit[section]?.link;
  return (
    <div ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 }}>
      <div style={{ ...S.card, opacity: isHidden ? 0.45 : 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
          {manageMode && (
            <button ref={setActivatorNodeRef} {...listeners} {...attributes}
              style={{ background: 'none', border: 'none', cursor: 'grab', padding: 2, color: '#9E9C96', lineHeight: 1, flexShrink: 0, touchAction: 'none' }}>
              <GripVertical size={13} />
            </button>
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            {hasLink
              ? <a href={kit[section].link} target="_blank" rel="noreferrer" style={{ ...S.eyebrow, color: '#21302A', textDecoration: 'none' }}>{label}</a>
              : <span style={S.eyebrow}>{label}</span>
            }
          </div>
          {manageMode && (
            <button onClick={onToggleHide} title={isHidden ? 'Show card' : 'Hide card'}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: isHidden ? '#9E9C96' : '#21302A', lineHeight: 1, flexShrink: 0 }}>
              {isHidden ? <EyeOff size={13} /> : <Eye size={13} />}
            </button>
          )}
        </div>
        {!manageMode && (() => {
          const schemaSubfolders = sectionSchema?.subfolders || [];
          // Schema subfolders keep their schema order below (Plans, Sections,
          // Elevations, Masterplan is meaningful); only the ones discovered in
          // Dropbox need sorting, since those arrive in no order at all.
          const discoveredSubfolders = (mainSf?.entries || [])
            .filter(e => e.is_folder && !schemaSubfolders.some(s => s.toLowerCase() === e.name.toLowerCase()))
            .sort(compareByName)
            .map(e => e.name);
          const sectionFolderName = sectionSchema?.folder || '';
          const approvedNames = approvedSubfolders?.[sectionFolderName] || [];
          // A section that allows any subfolder treats everything found in it
          // as approved, so nothing there is drawn in the gold "unexpected" tone.
          const allSubfolders = [
            ...schemaSubfolders.map(name => ({ name, discovered: false, approved: false })),
            ...discoveredSubfolders.map(name => ({
              name,
              discovered: true,
              approved: !!sectionSchema?.allowAnySubfolders
                || approvedNames.some(a => a.toLowerCase() === name.toLowerCase()),
            })),
          ];
          if (allSubfolders.length === 0) return null;
          // Resolution variants sitting directly under a section (Drawings →
          // jpeg-highres / tiff) follow the same setting as variants deeper in
          // the tree: one row for the chosen version, none for the others.
          const sectionPath = `${BRIO_PRESS_BASE}/${projectFolderName}/${dropboxFolder}`;
          const shownSubfolders = filterVariantFolders(allSubfolders, s => s.name, versionPref);
          return (
            <div style={{ marginBottom: 8 }}>
              {shownSubfolders.map(({ name: subName, discovered, approved }) => {
                const sfKey = `${section}__${subName}`;
                const sfExpanded = !!sectionExpanded[sfKey];
                const labelColor = !discovered ? '#9E9C96' : approved ? '#9E9C96' : '#9F902A';
                const sfPath = `${sectionPath}/${subName}`;
                return (
                  <div key={subName} style={{ paddingTop: 5, borderTop: '1px solid rgba(27,27,25,.06)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: sfExpanded ? 6 : 0 }}>
                      <FolderName name={subName} path={sfPath} color={labelColor} onOpenFolder={onOpenFolder} />
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        {(() => {
                          // A subfolder you added in Dropbox (a photographer, a
                          // discipline) is short of the version folders the
                          // schema wants. Offer to fill them in here rather than
                          // making a full project sync the only way to get them.
                          if (!onFillTemplate) return null;
                          const gaps = missingTemplateFolders(sectionSchema, sectionFiles[sfKey]);
                          if (gaps.length === 0) return null;
                          const busy = fillingTemplate === sfKey;
                          return (
                            <button
                              onClick={() => onFillTemplate({ sfKey, section, subName, sfPath, count: gaps.length })}
                              disabled={busy}
                              title={`Create ${gaps.join(', ')} inside ${subName}`}
                              style={{ background: 'none', border: 'none', padding: 0, cursor: busy ? 'default' : 'pointer', color: GOLD, fontFamily: "'IBM Plex Mono', monospace", fontSize: 9, letterSpacing: '0.06em', textTransform: 'uppercase', opacity: busy ? 0.5 : 1 }}
                            >
                              {busy ? 'Adding…' : `+ ${gaps.length} version folder${gaps.length === 1 ? '' : 's'}`}
                            </button>
                          );
                        })()}
                        <button onClick={() => onToggleFiles(sfKey, sfPath)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 1, color: '#9E9C96', lineHeight: 1 }}>
                          {sfExpanded ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
                        </button>
                      </span>
                    </div>
                    {sfExpanded && (
                      <div style={{ marginBottom: 4 }}>
                        <FolderTree
                          folderKey={sfKey}
                          folderPath={sfPath}
                          sectionFiles={sectionFiles}
                          sectionExpanded={sectionExpanded}
                          onToggleFiles={onToggleFiles}
                          versionPref={versionPref}
                          imgCols={imgCols}
                          onOpenLightbox={onOpenLightbox}
                          onOpenFolder={onOpenFolder}
                          selection={selection}
                          tagsByAsset={tagsByAsset}
                          rankView={rankView}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })()}
        {section === 'release' && !manageMode && (
          <NoteArea value={kit.release?.text || ''} onChange={v => saveKit('release', 'text', v)} placeholder="Paste press release text…" minHeight={80} />
        )}
        {expanded && !manageMode && (
          <div style={{ marginTop: 10, borderTop: '1px solid rgba(27,27,25,.08)', paddingTop: 10 }}>
            {renderFileContents(mainSf, imgCols, onOpenLightbox, `${BRIO_PRESS_BASE}/${projectFolderName}/${dropboxFolder}`, selection, tagsByAsset, rankView)}
          </div>
        )}
      </div>
    </div>
  );
}

// ── FOLDER BROWSING STATE ─────────────────────────────────────────────────
// Lazy-loading Dropbox folder tree state: sectionFiles/sectionExpanded keyed
// by `key` (nested folders as `parentKey__childName`). expandSections turns on
// cascade mode and loads a list of top-level folders; toggleExpandAll flips
// between everything-open and everything-closed for the given top level.

// Folder listings survive navigation. They used to live only in component
// state, so leaving a project and coming back re-fetched every section from
// Dropbox — the single biggest source of repeat egress on this page.
//
// The TTL is bounded by the perishable part of a listing. Thumbnail URLs point
// at the comms-assets bucket and never expire, and previewable files no longer
// carry a link at all — but the plain file list still does, and a Dropbox
// temporary link is only good for about four hours. Raising this stays safe up
// to that ceiling; the cost is that a file added in Dropbox takes longer to
// appear for anyone who does not press refresh.
const LISTING_TTL_MS = 30 * 60 * 1000;
const listingCache = new Map(); // folderPath -> { entries, at }

function readCachedListing(folderPath) {
  const hit = listingCache.get(folderPath);
  if (!hit) return null;
  if (Date.now() - hit.at > LISTING_TTL_MS) {
    listingCache.delete(folderPath);
    return null;
  }
  return hit.entries;
}

/** Drop every cached listing so the next open re-reads Dropbox. */
export function clearListingCache() {
  listingCache.clear();
}

/** Drop one folder's cached listing — used after we create folders inside it. */
export function invalidateListing(folderPath) {
  listingCache.delete(folderPath);
}

/**
 * Applies a completed rename to a cached listing instead of dropping it.
 *
 * Dropping it costs a cold re-read of the whole folder — and that read is not
 * a cheap directory listing, it re-resolves every thumbnail in the folder, so
 * a rename of ten files was followed by ten-plus seconds of staring at the old
 * names. But a rename changes exactly one thing about an entry, its name, and
 * the server has just told us the new one. Nothing else in the entry moves:
 * `file_id` survives a rename by definition, and `thumb_url` points at a
 * content-hash-keyed object that a rename cannot invalidate.
 *
 * Entries carry no path of their own — callers derive it as `folder/name`
 * (see the pool builder in reviewMode) — so rewriting `name` is the whole job.
 *
 * Only ever called with renames the server confirmed. A file whose move failed
 * keeps its old name, which is exactly what the cache already holds.
 */
export function renameCachedEntries(folderPath, pairs) {
  const hit = listingCache.get(folderPath);
  if (!hit || !pairs?.length) return;
  const next = new Map(pairs.map(p => [p.from, p.to]));
  hit.entries = hit.entries.map(e => {
    const to = next.get(e.name);
    return to ? { ...e, name: to } : e;
  });
}

/**
 * One folder's listing, from the shared cache when it is warm. Same request
 * shape as the browser's own loader so the two share cache entries — a folder
 * already open in the tree costs the review mode nothing to pull in.
 */
export async function listFolder(folderPath) {
  const cached = readCachedListing(folderPath);
  if (cached) return cached;
  const json = await withListingSlot(() => callEdgeFunction('list-press-folder-contents', { folderPath, previewLinks: false }));
  const entries = json.entries || [];
  listingCache.set(folderPath, { entries, at: Date.now() });
  return entries;
}

export function usePressFolderBrowser(versionPref = VERSION_PREF_AUTO) {
  const [sectionExpanded, setSectionExpanded] = useState({});
  const [sectionFiles, setSectionFiles] = useState({});
  const [expandAll, setExpandAll] = useState(false);
  const expandAllRef = useRef(false);
  const loadingKeysRef = useRef(new Set());
  // The cascade runs inside async callbacks, so it reads the live preference
  // from a ref rather than the value captured when the load started.
  const versionPrefRef = useRef(versionPref);
  versionPrefRef.current = versionPref;

  // Subfolders the cascade should open: all of them bar the image versions the
  // "Image versions" setting isn't showing, which are the same pictures again.
  const cascadeFolders = (entries) => {
    const folders = (entries || []).filter(e => e.is_folder);
    const skip = skippedVariantNames(folders.map(e => e.name), versionPrefRef.current);
    return folders.filter(e => !skip.has(e.name));
  };

  // Opening the cascade into a listing that is already in hand, so a cached
  // folder still reveals its subfolders without a round trip.
  const cascadeInto = (fileKey, folderPath, entries) => {
    if (!expandAllRef.current) return;
    cascadeFolders(entries).forEach(e => {
      const childKey = `${fileKey}__${e.name}`;
      const childPath = `${folderPath}/${e.name}`;
      setSectionExpanded(s => ({ ...s, [childKey]: true }));
      loadSectionFiles(childKey, childPath);
    });
  };

  const loadSectionFiles = async (fileKey, folderPath) => {
    if (loadingKeysRef.current.has(fileKey)) return;

    const cached = readCachedListing(folderPath);
    if (cached) {
      setSectionFiles(s => ({ ...s, [fileKey]: { loading: false, entries: cached, error: null, path: folderPath } }));
      cascadeInto(fileKey, folderPath, cached);
      return;
    }

    loadingKeysRef.current.add(fileKey);
    // `path` is kept alongside the listing so the effect below can open a
    // subfolder without the component that loaded it being on screen.
    setSectionFiles(s => ({ ...s, [fileKey]: { loading: true, entries: [], error: null, path: folderPath } }));
    try {
      const json = await withListingSlot(() => callEdgeFunction('list-press-folder-contents', { folderPath, previewLinks: false }));
      const entries = json.entries || [];
      listingCache.set(folderPath, { entries, at: Date.now() });
      loadingKeysRef.current.delete(fileKey);
      setSectionFiles(s => ({ ...s, [fileKey]: { loading: false, entries, error: null, path: folderPath } }));
      cascadeInto(fileKey, folderPath, entries);
    } catch (e) {
      loadingKeysRef.current.delete(fileKey);
      setSectionFiles(s => ({ ...s, [fileKey]: { loading: false, entries: [], error: e.message, path: folderPath } }));
    }
  };

  const toggleSectionFiles = async (fileKey, folderPath) => {
    const isOpen = sectionExpanded[fileKey];
    setSectionExpanded(s => ({ ...s, [fileKey]: !isOpen }));
    if (!isOpen && !sectionFiles[fileKey] && !loadingKeysRef.current.has(fileKey)) {
      await loadSectionFiles(fileKey, folderPath);
    }
  };

  // Whichever image version is being shown is opened for the user: it is the
  // only one of its siblings on screen, so leaving it collapsed would hide the
  // pictures behind a pointless click. Runs as listings arrive — so a chosen
  // folder that turns out to be empty hands over to the next best version —
  // and again whenever the setting changes.
  useEffect(() => {
    Object.entries(sectionFiles).forEach(([key, sf]) => {
      if (!sf || sf.loading || !sf.entries?.length || !sf.path) return;
      if (!sectionExpanded[key]) return; // parent is closed — nothing to reveal
      const names = sf.entries
        .filter(e => e.is_folder)
        .map(e => e.name)
        .filter(name => {
          const child = sectionFiles[`${key}__${name}`];
          return !(child && !child.loading && child.entries && child.entries.length === 0);
        });
      const chosen = chosenVariant(names, versionPref);
      if (!chosen) return;
      const childKey = `${key}__${chosen}`;
      if (sectionExpanded[childKey] && sectionFiles[childKey]) return;
      setSectionExpanded(s => (s[childKey] ? s : { ...s, [childKey]: true }));
      if (!sectionFiles[childKey] && !loadingKeysRef.current.has(childKey)) {
        loadSectionFiles(childKey, `${sf.path}/${chosen}`);
      }
    });
  }, [sectionFiles, sectionExpanded, versionPref]); // eslint-disable-line react-hooks/exhaustive-deps

  // Open the given top-level folders and cascade into their subfolders.
  const expandSections = (folders) => {
    setExpandAll(true);
    expandAllRef.current = true;
    folders.forEach(({ key, path }) => {
      setSectionExpanded(prev => ({ ...prev, [key]: true }));
      loadSectionFiles(key, path);
    });
  };

  const toggleExpandAll = (folders) => {
    if (expandAll) {
      setSectionExpanded({});
      setExpandAll(false);
      expandAllRef.current = false;
      loadingKeysRef.current.clear();
    } else {
      setExpandAll(true);
      expandAllRef.current = true;
      const currentFiles = sectionFiles;
      const cascadeLoaded = (fileKey, folderPath) => {
        const sf = currentFiles[fileKey];
        if (!sf || sf.loading || !sf.entries) return;
        cascadeFolders(sf.entries).forEach(e => {
          const childKey = `${fileKey}__${e.name}`;
          const childPath = `${folderPath}/${e.name}`;
          setSectionExpanded(s => ({ ...s, [childKey]: true }));
          if (!currentFiles[childKey] && !loadingKeysRef.current.has(childKey)) {
            loadSectionFiles(childKey, childPath);
          } else {
            cascadeLoaded(childKey, childPath);
          }
        });
      };
      folders.forEach(({ key, path }) => {
        setSectionExpanded(prev => ({ ...prev, [key]: true }));
        if (!currentFiles[key] && !loadingKeysRef.current.has(key)) {
          loadSectionFiles(key, path);
        } else {
          cascadeLoaded(key, path);
        }
      });
    }
  };

  // Drop the cache and re-read every folder currently on screen, for when
  // someone has just added files in Dropbox and wants them now.
  const refreshFolders = () => {
    clearListingCache();
    const open = Object.entries(sectionFiles)
      .filter(([, sf]) => sf?.path)
      .map(([key, sf]) => ({ key, path: sf.path }));
    loadingKeysRef.current.clear();
    setSectionFiles({});
    open.forEach(({ key, path }) => loadSectionFiles(key, path));
  };

  return { sectionExpanded, sectionFiles, expandAll, loadSectionFiles, toggleSectionFiles, expandSections, toggleExpandAll, refreshFolders };
}

// ── ASSET SELECTION ───────────────────────────────────────────────────────
// Select-mode state shared by every surface that lets the user pick assets
// (project press kits now, theme sections in step 2). `selection` is the
// object renderFileContents expects, or null when not selecting.

// `allowFiles` lets any file in the plain list be selected, not only PDFs and
// videos. The project press kit turns it on so documents can be shared; theme
// pages leave it off, since everything they do with a selection is visual.
export function useAssetSelection({ allowFiles = false } = {}) {
  const [selectMode, setSelectMode] = useState(false);
  const [selectedAssets, setSelectedAssets] = useState({}); // dropbox path -> { name, thumbUrl, isVideo, isPdf?, isFile? }

  const selection = selectMode ? {
    active: true,
    allowFiles,
    has: path => !!selectedAssets[path],
    toggle: (path, meta) => setSelectedAssets(s => {
      const next = { ...s };
      if (next[path]) delete next[path]; else next[path] = meta;
      return next;
    }),
  } : null;
  const selectedList = Object.entries(selectedAssets).map(([path, meta]) => ({ path, ...meta }));

  const exitSelectMode = () => { setSelectMode(false); setSelectedAssets({}); };
  const toggleSelectMode = () => { if (selectMode) exitSelectMode(); else setSelectMode(true); };

  return { selectMode, selection, selectedList, toggleSelectMode, exitSelectMode };
}

// Floating action bar shown while select mode is active. `actions` is a list
// of { label, onClick } buttons that appear once something is selected.
//
// Every button keeps its label on one line. Without that, a two-word label
// ("Assign to post") wrapped while the one-word ones did not, so those buttons
// stood a line taller than the rest and the bar looked broken. Holding the
// labels on one line makes the bar as wide as its contents, so the bar itself
// wraps into rows and is capped to the viewport rather than running off the
// edge of a narrow window.
export function SelectBar({ count, emptyText, actions, onCancel }) {
  const btnStyle = {
    fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, letterSpacing: '0.06em',
    textTransform: 'uppercase', borderRadius: 3, padding: '7px 12px', cursor: 'pointer',
    whiteSpace: 'nowrap',
  };
  return (
    <div role="toolbar" aria-label="Actions for the selected files"
      style={{ position: 'fixed', bottom: 20, left: '50%', transform: 'translateX(-50%)', zIndex: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap', maxWidth: 'calc(100vw - 32px)', gap: 12, background: '#21302A', color: '#F7F6F2', borderRadius: 4, padding: '10px 16px', boxShadow: '0 10px 30px rgba(0,0,0,.3)' }}>
      <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 12 }}>
        {count === 0 ? emptyText : `${count} selected`}
      </span>
      {count > 0 && actions.map(a => (
        <button key={a.label} onClick={a.onClick} style={{ ...btnStyle, background: GOLD, color: '#fff', border: 'none' }}>
          {a.label}
        </button>
      ))}
      <button onClick={onCancel} style={{ ...btnStyle, background: 'none', color: '#C8C4BB', border: '1px solid rgba(247,246,242,.3)' }}>
        Cancel
      </button>
    </div>
  );
}

// ── ASSIGN PRESS-KIT ASSETS TO A SOCIAL POST ──────────────────────────────

// Photography files sit in /BRIO-press/<project>/<Photography>/<Photographer>/file.jpg —
// the subfolder is the photographer. Everything else defaults to the studio credit.
export function detectCredit(dropboxPath, kitSchema) {
  const rel = dropboxPath.startsWith(BRIO_PRESS_BASE + '/') ? dropboxPath.slice(BRIO_PRESS_BASE.length + 1) : dropboxPath;
  const segments = rel.split('/');
  const photoFolder = (kitSchema.find(s => s.key === 'photos')?.folder || 'Photography').toLowerCase();
  if (segments.length >= 4 && segments[1]?.toLowerCase() === photoFolder) return segments[2];
  return 'Architecture BRIO';
}

// Assets with no fileId are skipped rather than inserted under a path that
// would go stale on the next rename; the caller reports the shortfall.
export async function addAssetsToPost(postId, assets, kitSchema, projectCode) {
  const usable = assets.filter(a => a.fileId);
  if (!usable.length) return 0;
  // Append after the post's current highest sort_order
  const { data: existing } = await comms.from('social_post_images')
    .select('sort_order').eq('post_id', postId)
    .order('sort_order', { ascending: false }).limit(1);
  let nextOrder = existing?.length ? (existing[0].sort_order || 0) + 1 : 0;
  for (let i = 0; i < usable.length; i++) {
    const a = usable[i];
    // Listings and theme highlights both carry a comms-assets thumbnail URL,
    // so there is nothing to upload here — the cache is already populated.
    const thumbUrl = a.thumbUrl || null;
    await commsInsert('social_post_images', {
      comms_id: `spi-${Date.now()}-${i}`,
      post_id: postId,
      dropbox_file_id: a.fileId,
      dropbox_path: a.path,
      file_name: a.name,
      is_video: !!a.isVideo,
      sort_order: nextOrder++,
      thumb_url: thumbUrl,
      credit: detectCredit(a.path, kitSchema),
      project_code: a.projectCode || projectCode || null,
      crops: {},
    });
  }
  return usable.length;
}

// Pin assets to a theme's highlights (comms.theme_assets). Same shape as
// addAssetsToPost; `section` scopes the pool (null for plain themes) and each
// asset may carry its own projectCode (themes mix assets from many projects).
// `autoTags` (from themeTagsFor) are stamped onto comms.asset_tags so the
// asset shows its theme membership as hashtags everywhere it appears.
// Assets with no fileId are skipped, as in addAssetsToPost.
export async function addAssetsToTheme(themeId, section, assets, kitSchema, autoTags = []) {
  const usable = assets.filter(a => a.fileId);
  if (!usable.length) return 0;
  let q = comms.from('theme_assets').select('sort_order').eq('theme_id', themeId);
  q = section == null ? q.is('section', null) : q.eq('section', section);
  const { data: existing } = await q.order('sort_order', { ascending: false }).limit(1);
  let nextOrder = existing?.length ? (existing[0].sort_order || 0) + 1 : 0;
  const enriched = [];
  for (let i = 0; i < usable.length; i++) {
    const a = usable[i];
    const thumbUrl = a.thumbUrl || null; // reuse the cached thumbnail when re-assigning
    await commsInsert('theme_assets', {
      comms_id: `ta-${Date.now()}-${i}`,
      theme_id: themeId,
      section: section ?? null,
      dropbox_file_id: a.fileId,
      dropbox_path: a.path,
      file_name: a.name,
      is_video: !!a.isVideo,
      sort_order: nextOrder++,
      thumb_url: thumbUrl,
      credit: detectCredit(a.path, kitSchema),
      project_code: a.projectCode || null,
    });
    enriched.push({ ...a, thumbUrl });
  }
  if (autoTags.length) {
    try { await updateAssetTags(enriched, autoTags); } catch { /* tags are a convenience layer */ }
  }
  return usable.length;
}

// ── ASSET HASHTAGS ────────────────────────────────────────────────────────
// comms.asset_tags is a registry keyed by dropbox_path: hashtags plus the
// cached-asset fields so tagged assets render without touching Dropbox.

export function slugTag(s) {
  return (s || '').toLowerCase().replace(/^#+/, '').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

// Hashtags an asset gets when assigned to a theme section: the theme id plus
// the sub-tag (material sub-tab / practice section / collaborator name slug).
export function themeTagsFor(themeId, section, sectionLabel) {
  if (!section) return [themeId];
  if (themeId === 'collaborators') return [themeId, slugTag(sectionLabel) || section];
  return [themeId, section];
}

// Merge/remove tags on each asset's registry row; rows are created on first
// tag and deleted when their last tag is removed.
//
// Deleting the row on the last tag is deliberate and is why ranks live in their
// own table rather than as a column here: a rank kept alongside the tags would
// be destroyed by an unrelated hashtag edit (see the asset_ranks migration
// header). Do not soften this into a soft delete.
//
// Assets with no fileId are skipped, exactly as updateAssetRanks skips them —
// writing one under its path would re-create the orphaning this table was just
// migrated out of. Returns how many were actually tagged so a caller can say so.
export async function updateAssetTags(assets, addTags, removeTags = []) {
  if (!assets.length || (!addTags.length && !removeTags.length)) return 0;
  const taggable = assets.filter(a => a.fileId);
  if (!taggable.length) return 0;
  const ids = taggable.map(a => a.fileId);
  const { data: rows } = await comms.from('asset_tags').select('*').in('dropbox_file_id', ids);
  const byId = {};
  (rows || []).forEach(r => { byId[r.dropbox_file_id] = r; });
  for (let i = 0; i < taggable.length; i++) {
    const a = taggable[i];
    const row = byId[a.fileId];
    const merged = [...new Set([...(row?.tags || []), ...addTags])].filter(t => !removeTags.includes(t));
    if (merged.length === 0) {
      if (row) await commsDelete('asset_tags', 'dropbox_file_id', a.fileId);
      continue;
    }
    const thumbUrl = row?.thumb_url || a.thumbUrl || null;
    await commsUpsert('asset_tags', {
      dropbox_file_id: a.fileId,
      // Refreshed on every write, so the last known location keeps up with the
      // file even though nothing matches on it any more. A caller that knows
      // the file only by id (the deck picker reads pooled rank and tag rows,
      // and a rank row carries no path) leaves what is stored rather than
      // blanking the one handle an id-less listing can still match on.
      dropbox_path: a.path || row?.dropbox_path || null,
      file_name: a.name || row?.file_name || a.path.split('/').pop(),
      is_video: !!(a.isVideo ?? row?.is_video),
      thumb_url: thumbUrl,
      project_code: a.projectCode || row?.project_code || null,
      tags: merged,
      updated_at: new Date().toISOString(),
    }, 'dropbox_file_id');
  }
  return taggable.length;
}

/**
 * Strip one hashtag from specific rows, addressed by Dropbox file id.
 *
 * updateAssetTags is the path for tagging assets you are looking at: it
 * rewrites the cached fields from the listing entry it was handed. This one is
 * for taking a tag off ids you know but hold no listing for — chiefly the other
 * resolutions of a photograph, which projectAssets pools into one record. Untag
 * only the copy the deck picked and a sibling copy keeps the tag, so the pooled
 * record still reads as tagged and the chip springs back on the next load.
 *
 * Touches `tags` alone, so a row's path, name and thumbnail survive a removal
 * made from a screen that never saw the file. The row is deleted when its last
 * tag goes, same rule as updateAssetTags. Returns how many rows changed.
 */
export async function removeTagFromIds(fileIds, tag) {
  const ids = [...new Set((fileIds || []).filter(Boolean))];
  if (!ids.length || !tag) return 0;
  const { data: rows } = await comms.from('asset_tags').select('dropbox_file_id, tags').in('dropbox_file_id', ids);
  let changed = 0;
  for (const row of rows || []) {
    if (!(row.tags || []).includes(tag)) continue;
    const merged = (row.tags || []).filter(t => t !== tag);
    if (merged.length === 0) await commsDelete('asset_tags', 'dropbox_file_id', row.dropbox_file_id);
    else await commsUpdate('asset_tags', 'dropbox_file_id', row.dropbox_file_id, { tags: merged, updated_at: new Date().toISOString() });
    changed++;
  }
  return changed;
}

// All tag rows for overlaying on file tiles, keyed by Dropbox file id AND by
// last known path, both pointing at the same tags array. Bump `rev` after
// tagging to refetch.
//
// Two keys in one map rather than two maps: the id is what the overlay should
// use, and does use whenever the listing carries one, so tags follow a file
// through a rename. But a listing served by an older list-press-folder-contents
// carries no file id at all, and for those entries the path is the only handle
// there is — showing the tags a stale-but-still-accurate path resolves to beats
// showing none. The two key spaces cannot collide: a Dropbox id always begins
// "id:", a press path always "/".
//
// See tagsForAsset for why the path is a fallback for id-less entries only and
// never a second guess for an entry whose id simply did not match.
export function useAssetTags(rev = 0) {
  const [tagsByAsset, setTagsByAsset] = useState({});
  useEffect(() => {
    let cancelled = false;
    comms.from('asset_tags').select('dropbox_file_id, dropbox_path, tags').then(({ data }) => {
      if (cancelled) return;
      const m = {};
      (data || []).forEach(r => {
        const tags = r.tags || [];
        if (r.dropbox_file_id) m[r.dropbox_file_id] = tags;
        if (r.dropbox_path) m[r.dropbox_path] = tags;
      });
      setTagsByAsset(m);
    });
    return () => { cancelled = true; };
  }, [rev]);
  return tagsByAsset;
}

/**
 * One asset's tags: by file id where the listing carries one, else by last
 * known path.
 *
 * An entry that has a file id is answered by that id alone — no match means no
 * tags, full stop. Falling through to the path there would be actively wrong:
 * rename a tagged file in Finder and give a second, untagged file its old name,
 * and the path lookup would decorate the newcomer with the first file's tags.
 * The path is a fallback for entries that have no id to match on, not a second
 * guess for entries whose id came back empty.
 */
export function tagsForAsset(tagsByAsset, fileId, path) {
  if (!tagsByAsset) return undefined;
  if (fileId) return tagsByAsset[fileId];
  return path ? tagsByAsset[path] : undefined;
}

// ── ASSET RANKS (PUBLICATION SHORTLIST) ───────────────────────────────────
// A 1–5 rank per asset, held in comms.asset_ranks and set only here in the
// app. Nothing writes it into the image files themselves — if that is added
// later it is a one-way projection of this table, never a second input, so
// there is exactly one place a rank can be true.
//
// Keyed on Dropbox's file id rather than the path: paths change whenever
// somebody moves or renames a file in Finder, and the team does that routinely.
// A listing served by a list-press-folder-contents older than migration
// 20260823000001 carries no file id; those entries simply show no stars and
// cannot be ranked, rather than falling back to a key that would go stale.

// "Shortlisted for publication" is a threshold on the rank, not a separate
// flag — one number to keep true instead of a number and a boolean that can
// drift apart.
export const SHORTLIST_MIN_RANK = 4;

/**
 * All ranks as { dropbox_file_id -> rank }. Bump `rev` after a batch edit to
 * refetch. `applyRank` writes one rank into local state straight away: culling
 * a shoot means rating image after image, and waiting on a refetch between each
 * one makes the stars lag behind the clicks.
 */
export function useAssetRanks(rev = 0) {
  const [ranksById, setRanksById] = useState({});
  useEffect(() => {
    let cancelled = false;
    comms.from('asset_ranks').select('dropbox_file_id, rank').then(({ data }) => {
      if (cancelled) return;
      const m = {};
      (data || []).forEach(r => { m[r.dropbox_file_id] = r.rank; });
      setRanksById(m);
    });
    return () => { cancelled = true; };
  }, [rev]);

  const applyRank = useCallback((fileId, rank) => {
    setRanksById(m => {
      const next = { ...m };
      if (rank) next[fileId] = rank; else delete next[fileId];
      return next;
    });
  }, []);

  return { ranksById, applyRank };
}

/**
 * Five clickable stars plus an explicit clear.
 *
 * Clicking the star that is already set also clears, which is the Bridge and
 * Lightroom convention — but on its own that is undiscoverable, and it cannot
 * clear at all when the picker is driving a multi-image selection, where
 * `value` is 0 because the selection has no single rank. So the ✕ is always
 * rendered and always clears, whatever `value` says.
 */
export function StarPicker({ value = 0, onChange, onDark = false, size = 16 }) {
  const [hover, setHover] = useState(0);
  const shown = hover || value;
  const idle = onDark ? 'rgba(255,255,255,.35)' : 'rgba(27,27,25,.22)';
  const [clearHot, setClearHot] = useState(false);
  return (
    <span onMouseLeave={() => { setHover(0); setClearHot(false); }} style={{ display: 'inline-flex', alignItems: 'center' }}>
      {[1, 2, 3, 4, 5].map(n => (
        <button key={n} type="button"
          title={n === value ? 'Clear the rank' : `${n} star${n === 1 ? '' : 's'}`}
          onMouseEnter={() => setHover(n)}
          onClick={e => { e.preventDefault(); e.stopPropagation(); onChange(n === value ? 0 : n); }}
          style={{ background: 'none', border: 'none', padding: '0 1px', cursor: 'pointer', lineHeight: 1, fontSize: size, color: n <= shown ? GOLD : idle }}>
          {'\u2605'}
        </button>
      ))}
      <button type="button"
        title="Clear the rank (0)"
        aria-label="Clear the rank"
        onMouseEnter={() => { setHover(0); setClearHot(true); }}
        onFocus={() => setClearHot(true)}
        onBlur={() => setClearHot(false)}
        onClick={e => { e.preventDefault(); e.stopPropagation(); onChange(0); }}
        style={{ background: 'none', border: 'none', padding: '0 2px', marginLeft: 5, cursor: 'pointer', lineHeight: 1, fontSize: Math.round(size * 0.8), color: clearHot ? (onDark ? '#fff' : '#1B1B19') : idle }}>
        {'\u00D7'}
      </button>
    </span>
  );
}

/**
 * Right-click-to-rank for asset tiles. The menu is owned by the page rather
 * than the tile because renderFileContents is a plain function with nowhere to
 * keep state; tiles just call openRankMenu(event, asset).
 */
export function useRankMenu({ ranksById, onRate }) {
  const [menu, setMenu] = useState(null); // { x, y, asset }

  useEffect(() => {
    if (!menu) return;
    const close = () => setMenu(null);
    const onKey = e => { if (e.key === 'Escape') close(); };
    window.addEventListener('click', close);
    window.addEventListener('contextmenu', close);
    // Capture phase: the menu is positioned in viewport coordinates, so any
    // scroll underneath it would leave it pointing at the wrong tile.
    window.addEventListener('scroll', close, true);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('click', close);
      window.removeEventListener('contextmenu', close);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('keydown', onKey);
    };
  }, [menu]);

  const openRankMenu = useCallback((ev, asset) => {
    ev.preventDefault();
    ev.stopPropagation();
    setMenu({ x: ev.clientX, y: ev.clientY, asset });
  }, []);

  const rankMenu = menu ? (
    <div onClick={e => e.stopPropagation()} onContextMenu={e => e.preventDefault()}
      style={{ position: 'fixed', left: Math.min(menu.x, window.innerWidth - 170), top: Math.min(menu.y, window.innerHeight - 70), zIndex: 10000, background: '#fff', border: '1px solid rgba(27,27,25,.15)', borderRadius: 4, boxShadow: '0 8px 24px rgba(0,0,0,.18)', padding: '8px 10px' }}>
      <div style={{ ...S.eyebrow, fontSize: 8, marginBottom: 4 }}>Rank for publication</div>
      {menu.asset.fileId
        ? <StarPicker value={ranksById?.[menu.asset.fileId] || 0}
            onChange={r => { onRate(menu.asset, r); setMenu(null); }} />
        : <div style={{ ...S.meta, fontSize: 11 }}>Reload the folder to rank this file.</div>}
    </div>
  ) : null;

  return { openRankMenu, rankMenu };
}

/**
 * Set every given asset to `rank`. Rank 0 clears it — unranked is the absence
 * of a row, so a shortlist query never has to filter zeros out.
 * Assets without a fileId are skipped (see the note above).
 */
export async function updateAssetRanks(assets, rank) {
  const rateable = assets.filter(a => a.fileId);
  if (!rateable.length) return 0;
  const now = new Date().toISOString();
  const { data: { user } } = await supabase.auth.getUser();
  for (const a of rateable) {
    if (!rank) {
      await commsDelete('asset_ranks', 'dropbox_file_id', a.fileId);
      continue;
    }
    await commsUpsert('asset_ranks', {
      dropbox_file_id: a.fileId,
      dropbox_path: a.path,
      file_name: a.name || a.path.split('/').pop(),
      is_video: !!a.isVideo,
      thumb_url: a.thumbUrl || null,
      project_code: a.projectCode || null,
      rank,
      rated_by: user?.id || null,
      rated_at: now,
      updated_at: now,
    }, 'dropbox_file_id');
  }
  return rateable.length;
}

/** Star badge on a tile. Nothing is drawn for an unranked asset. */
function RankStars({ rank }) {
  if (!rank) return null;
  return (
    <span title={`${rank} of 5`}
      style={{ position: 'absolute', left: 4, top: 4, fontSize: 9, letterSpacing: '0.5px', background: 'rgba(27,27,25,.65)', color: GOLD, borderRadius: 2, padding: '1px 4px', lineHeight: 1.4 }}>
      {'\u2605'.repeat(rank)}
    </span>
  );
}

/**
 * Set one rank across the whole selection. Deliberately not a per-tile hover
 * control: ranking a shortlist is a batch judgement, and the selection machinery
 * for it already exists.
 */
export function RankAssetsModal({ assets, ranksById, onClose, onDone, showToast }) {
  const rateable = assets.filter(a => a.fileId);
  const skipped = assets.length - rateable.length;
  const current = [...new Set(rateable.map(a => ranksById?.[a.fileId] || 0))];
  const [choice, setChoice] = useState(current.length === 1 ? current[0] : null);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (busy || choice === null) return;
    setBusy(true);
    try {
      const n = await updateAssetRanks(rateable, choice);
      onDone(choice, n);
    } catch (e) {
      showToast(e.message, 'error');
      setBusy(false);
    }
  };

  return (
    <CommsModal title="Rank for publication" onClose={onClose} width={420}>
      <div style={{ ...S.body, marginBottom: 4 }}>
        Set the rank of the {rateable.length} selected file{rateable.length === 1 ? '' : 's'}.
        {' '}{SHORTLIST_MIN_RANK} stars and above counts as shortlisted.
      </div>
      <div style={{ ...S.meta, fontSize: 11.5, marginBottom: 14 }}>
        Ranks live in this app only — they are not written into the image files, so
        nothing you send to a publisher carries them.
      </div>
      {skipped > 0 && (
        <div style={{ ...S.meta, fontSize: 11.5, color: '#7A6020', marginBottom: 12 }}>
          {skipped} file{skipped === 1 ? '' : 's'} cannot be ranked yet — reload the folder
          to pick up their Dropbox file ids.
        </div>
      )}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {[5, 4, 3, 2, 1].map(n => (
          <FilterBtn key={n} label={'\u2605'.repeat(n)} active={choice === n} onClick={() => setChoice(n)} />
        ))}
        <FilterBtn label="Clear" active={choice === 0} onClick={() => setChoice(0)} />
      </div>
      {current.length > 1 && (
        <div style={{ ...S.meta, fontSize: 11.5, marginTop: 10 }}>
          The selection currently holds mixed ranks; saving sets them all to the same value.
        </div>
      )}
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 18 }}>
        <BtnGhost onClick={onClose}>Cancel</BtnGhost>
        <BtnPrimary onClick={save}>{busy ? 'Saving…' : 'Save rank'}</BtnPrimary>
      </div>
    </CommsModal>
  );
}

// Toggle hashtags on the selected assets. Chips cover the allowed list
// (app_settings.commsAssetHashtags), theme auto-tags, and any tag already on
// the selection; pre-checked = every selected asset has it. Unchecking a
// pre-checked tag removes it from all selected assets.
export function TagAssetsModal({ assets, onClose, onDone, showToast }) {
  const [options, setOptions] = useState(null); // [{tag, group}]
  const [initial, setInitial] = useState(new Set());
  const [checked, setChecked] = useState({});
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    Promise.all([
      supabase.from('app_settings').select('value').eq('key', 'commsAssetHashtags').maybeSingle(),
      comms.from('themes').select('comms_id').order('sort_order'),
      comms.from('asset_tags').select('dropbox_file_id, tags').in('dropbox_file_id', assets.filter(a => a.fileId).map(a => a.fileId)),
    ]).then(([s, th, cur]) => {
      let allowed = [];
      try {
        const v = typeof s.data?.value === 'string' ? JSON.parse(s.data.value) : s.data?.value;
        if (Array.isArray(v)) allowed = v;
      } catch {}
      const themeTags = (th.data || []).map(t => t.comms_id);
      const onSelection = [...new Set((cur.data || []).flatMap(r => r.tags || []))];
      const seen = new Set();
      const opts = [];
      const push = (tag, group) => { if (!seen.has(tag)) { seen.add(tag); opts.push({ tag, group }); } };
      const alpha = (a, b) => a.localeCompare(b);
      [...allowed].sort(alpha).forEach(t => push(t, 'Hashtags'));
      [...themeTags].sort(alpha).forEach(t => push(t, 'Themes'));
      [...onSelection].sort(alpha).forEach(t => push(t, 'On this selection'));
      setOptions(opts);
      const curById = {};
      (cur.data || []).forEach(r => { curById[r.dropbox_file_id] = r.tags || []; });
      // A tag is pre-checked only when every selected asset already carries it,
      // so saving cannot silently strip it from the ones that do.
      const shared = new Set(opts.map(o => o.tag).filter(tag => assets.every(a => (curById[a.fileId] || []).includes(tag))));
      setInitial(shared);
      setChecked(Object.fromEntries([...shared].map(t => [t, true])));
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const addTags = Object.keys(checked).filter(t => checked[t] && !initial.has(t));
      const removeTags = [...initial].filter(t => !checked[t]);
      const tagged = await updateAssetTags(assets, addTags, removeTags);
      // A tag row is stored against a Dropbox file id, so an asset from a
      // listing that carried none cannot be tagged. Say so rather than
      // reporting a save that only partly happened.
      if (tagged < assets.length) {
        showToast(`${assets.length - tagged} file${assets.length - tagged === 1 ? '' : 's'} skipped — no Dropbox file id in this listing`, 'error');
      }
      onDone(addTags, removeTags);
    } catch (e) {
      showToast(e.message, 'error');
      setBusy(false);
    }
  };

  const q = query.trim().toLowerCase().replace(/^#+/, '');
  const groups = [];
  (options || []).forEach(o => {
    if (q && !o.tag.includes(q)) return;
    const g = groups.find(x => x.name === o.group);
    if (g) g.items.push(o.tag); else groups.push({ name: o.group, items: [o.tag] });
  });

  return (
    <CommsModal title="Hashtags" onClose={onClose} width={460}>
      <div style={{ ...S.body, marginBottom: 12 }}>
        Toggle hashtags for the {assets.length} selected file{assets.length === 1 ? '' : 's'}. Manage the allowed list on Practice → Comms assets.
      </div>
      {options === null && <div style={S.meta}>Loading…</div>}
      {options !== null && options.length === 0 && (
        <div style={S.meta}>No hashtags defined yet — add them on Practice → Comms assets.</div>
      )}
      {options !== null && options.length > 0 && (
        <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Type to find a hashtag…" autoFocus
          style={{ ...S.inputStyle, marginBottom: 12 }} />
      )}
      <div style={{ maxHeight: 300, overflowY: 'auto' }}>
        {options !== null && options.length > 0 && groups.length === 0 && <div style={S.meta}>No hashtag matches “{query}”.</div>}
        {groups.map(g => (
          <div key={g.name} style={{ marginBottom: 10 }}>
            <div style={{ ...S.eyebrow, fontSize: 9, marginBottom: 5 }}>{g.name}</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {g.items.map(tag => (
                <FilterBtn key={tag} label={`#${tag}`} active={!!checked[tag]}
                  onClick={() => setChecked(c => ({ ...c, [tag]: !c[tag] }))} />
              ))}
            </div>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 18 }}>
        <BtnGhost onClick={onClose}>Cancel</BtnGhost>
        <BtnPrimary onClick={save}>{busy ? 'Saving…' : 'Save hashtags'}</BtnPrimary>
      </div>
    </CommsModal>
  );
}

export function AssignToPostModal({ assets, posts, project, kitSchema, onClose, onDone, showToast }) {
  const [selectedPostId, setSelectedPostId] = useState('');
  const [newTitle, setNewTitle] = useState('');
  const [newDate, setNewDate] = useState('');
  const [newPlatforms, setNewPlatforms] = useState([]);
  const [busy, setBusy] = useState(false);

  const sortedPosts = [...(posts || [])].sort((a, b) => (b.post_date || '').localeCompare(a.post_date || ''));
  // Post boards hold images and videos; PDFs and other documents are skipped.
  const notPdf = assets.filter(a => !a.isPdf && !a.isFile && !isPdf(a.name || ''));
  const pdfCount = assets.length - notPdf.length;
  // A post image is stored against its Dropbox file id, so an asset from a
  // listing that carried none cannot be posted. Counted here rather than
  // dropped inside addAssetsToPost, so the button says how many will land.
  const postable = notPdf.filter(a => a.fileId);
  const noIdCount = notPdf.length - postable.length;

  const assign = async () => {
    if (busy) return;
    if (postable.length === 0) {
      showToast(pdfCount && !noIdCount
        ? 'Only documents selected — posts accept images and videos'
        : 'None of these files carry a Dropbox file id yet — re-open the folder to refresh the listing');
      return;
    }
    setBusy(true);
    try {
      let postId = selectedPostId;
      if (!postId) {
        if (!newTitle.trim() || !newDate) { showToast('Give the new post a title and a date'); setBusy(false); return; }
        const row = await commsInsert('social_cal', {
          comms_id: 'sc-' + Date.now(),
          title: newTitle.trim(),
          post_date: newDate,
          platforms: newPlatforms,
          variants: {},
          status: 'draft',
          project_code: project.project_code || null,
        });
        postId = row.comms_id;
      }
      await addAssetsToPost(postId, postable, kitSchema, project.project_code);
      onDone(postId);
    } catch (e) {
      showToast(e.message, 'error');
      setBusy(false);
    }
  };

  return (
    <CommsModal title="Assign to post" onClose={onClose}>
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 16 }}>
        {assets.map(a => a.thumbUrl
          ? <img key={a.path} src={a.thumbUrl} alt={a.name} title={a.name} loading="lazy" style={{ width: 52, height: 52, objectFit: 'cover', borderRadius: 2 }} />
          : <span key={a.path} title={a.name} style={{ width: 52, height: 52, borderRadius: 2, background: '#E8E5DD', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9A988F' }}><Film size={18} /></span>
        )}
      </div>
      <Field label="Add to an existing post">
        <select value={selectedPostId} onChange={e => { setSelectedPostId(e.target.value); }} style={S.inputStyle}>
          <option value="">— choose a post —</option>
          {sortedPosts.map(p => (
            <option key={p.comms_id} value={p.comms_id}>
              {(p.post_date || 'no date') + ' — ' + (p.title || '(untitled)') + (p.status ? ` · ${p.status}` : '')}
            </option>
          ))}
        </select>
      </Field>
      <div style={{ ...S.eyebrow, margin: '18px 0 10px' }}>… or create a new post</div>
      <CInput label="Post title" value={newTitle} onChange={v => { setNewTitle(v); setSelectedPostId(''); }} placeholder="e.g. Riparian House — monsoon light" />
      <Field label="Scheduled date">
        <input type="date" value={newDate} onChange={e => { setNewDate(e.target.value); setSelectedPostId(''); }} style={S.inputStyle} />
      </Field>
      <Field label="Platforms">
        <div style={{ display: 'flex', gap: 6 }}>
          {PLATFORMS.map(pl => (
            <FilterBtn key={pl} label={pl} active={newPlatforms.includes(pl)}
              onClick={() => setNewPlatforms(cur => cur.includes(pl) ? cur.filter(x => x !== pl) : [...cur, pl])} />
          ))}
        </div>
      </Field>
      {noIdCount > 0 && (
        <div style={{ ...S.meta, marginTop: 10, color: '#8A6A2A' }}>
          {noIdCount} file{noIdCount === 1 ? '' : 's'} will be skipped — no Dropbox file id in this listing. Re-open the folder to refresh it.
        </div>
      )}
      {pdfCount > 0 && (
        <div style={{ ...S.meta, marginTop: 10, color: '#8A6A2A' }}>
          {pdfCount} document{pdfCount === 1 ? '' : 's'} will be skipped — posts accept images and videos only.
        </div>
      )}
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
        <BtnGhost onClick={onClose}>Cancel</BtnGhost>
        <BtnPrimary onClick={assign}>{busy ? 'Assigning…' : `Assign ${postable.length} file${postable.length === 1 ? '' : 's'}`}</BtnPrimary>
      </div>
    </CommsModal>
  );
}
